import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { subscriptionsAPI, companyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Building2, CreditCard, Users, Mail, Phone, Globe, Calendar,
  ArrowLeft, Pencil, Ban, CheckCircle, RefreshCw, DollarSign,
  Clock, AlertTriangle, Trash2, LogIn, Eye,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

const STATUS_CONFIG = {
  active: { className: 'sa-badge-active', label: 'Active', color: '#16a34a' },
  trialing: { className: 'sa-badge-trialing', label: 'Trialing', color: '#2563eb' },
  pending: { className: 'sa-badge-pending', label: 'Pending', color: '#d97706' },
  past_due: { className: 'sa-badge-pending', label: 'Past Due', color: '#d97706' },
  expired: { className: 'sa-badge-expired', label: 'Expired', color: '#64748b' },
  cancelled: { className: 'sa-badge-cancelled', label: 'Cancelled', color: '#dc2626' },
  suspended: { className: 'sa-badge-suspended', label: 'Suspended', color: '#991b1b' },
};

export default function SuperAdminCompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [company, setCompany] = useState(null);
  const [plans, setPlans] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  // Superadmin: change this company's base currency
  const [currencies, setCurrencies] = useState([]);
  const [currencyCode, setCurrencyCode] = useState('');
  const [savingCurrency, setSavingCurrency] = useState(false);

  const load = useCallback(async () => {
    try {
      const [companies, p, a] = await Promise.all([
        subscriptionsAPI.companies({}),
        subscriptionsAPI.plans(),
        subscriptionsAPI.activity(),
      ]);
      const co = companies.find(c => c.id === parseInt(id));
      setCompany(co);
      setPlans(p);
      setActivity(a.filter(act => act.company_id === parseInt(id) || act.company_name === co?.name));
      if (co) setCurrencyCode(co.currency || co.base_currency || 'USD');
      companyAPI.currencies().then(setCurrencies).catch(() => {});
    } catch (err) {
      toast.error('Failed to load company details');
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const handleSaveCurrency = async () => {
    if (!currencyCode) return;
    setSavingCurrency(true);
    try {
      await companyAPI.setCompanyBaseCurrency(company.id, currencyCode);
      toast.success(`Base currency set to ${currencyCode}`);
    } catch (err) { toast.error(err.message); }
    setSavingCurrency(false);
  };

  const handleCancel = async () => {
    const reason = prompt('Cancellation reason:');
    if (reason === null) return;
    try {
      await subscriptionsAPI.cancel(company.sub_id, { reason: reason || 'Cancelled by admin' });
      toast.success('Subscription cancelled');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleReactivate = async () => {
    try {
      await subscriptionsAPI.reactivate(company.sub_id);
      toast.success('Subscription reactivated');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleConfirm = async () => {
    try {
      await subscriptionsAPI.confirm(company.sub_id);
      toast.success('Subscription confirmed!');
      load();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;

  if (!company) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Building2 size={48} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
        <h3 style={{ color: '#475569', margin: '0 0 8px' }}>Company Not Found</h3>
        <p style={{ color: '#94a3b8', fontSize: 13 }}>This company may have been removed.</p>
        <button className="sa-btn sa-btn-primary" style={{ marginTop: 12 }} onClick={() => navigate('/super-admin/companies')}>
          <ArrowLeft size={14} /> Back to Companies
        </button>
      </div>
    );
  }

  const st = STATUS_CONFIG[company.sub_status] || STATUS_CONFIG.expired;

  // Days remaining
  let daysLeft = null;
  if (company.current_period_end) {
    daysLeft = Math.max(0, Math.ceil((new Date(company.current_period_end) - new Date()) / 86400000));
  } else if (company.trial_ends_at) {
    daysLeft = Math.max(0, Math.ceil((new Date(company.trial_ends_at) - new Date()) / 86400000));
  }

  return (
    <div>
      {/* Back button */}
      <button className="sa-btn sa-btn-ghost" onClick={() => navigate('/super-admin/companies')} style={{ marginBottom: 16 }}>
        <ArrowLeft size={14} /> Back to Companies
      </button>

      {/* Header */}
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: 24,
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 18, fontWeight: 800,
            }}>
              {company.name[0]?.toUpperCase()}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{company.name}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span className={`sa-badge-status ${st.className}`}>{st.label}</span>
                {company.plan_name && (
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    Plan: <strong>{company.plan_name}</strong>
                  </span>
                )}
                {daysLeft !== null && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    background: daysLeft <= 7 ? '#fef2f2' : '#f0fdf4',
                    color: daysLeft <= 7 ? '#991b1b' : '#166534',
                  }}>
                    {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {/* LOGIN AS — view company's accounting data */}
            <button
              className="sa-btn sa-btn-primary"
              disabled={impersonating}
              onClick={async () => {
                setImpersonating(true);
                try {
                  await startImpersonation(company.id);
                  toast.success(`Now viewing ${company.name}'s accounting data`);
                  navigate('/dashboard');
                } catch (err) {
                  toast.error(err.message);
                  setImpersonating(false);
                }
              }}
            >
              <LogIn size={14} /> {impersonating ? 'Switching...' : 'Login As Company'}
            </button>
            {company.sub_status === 'pending' && company.sub_id && (
              <button className="sa-btn sa-btn-primary" onClick={handleConfirm}>
                <CheckCircle size={14} /> Confirm
              </button>
            )}
            {company.sub_status === 'active' && company.sub_id && (
              <button className="sa-btn sa-btn-danger" onClick={handleCancel}>
                <Ban size={14} /> Cancel Subscription
              </button>
            )}
            {(company.sub_status === 'cancelled' || company.sub_status === 'expired') && company.sub_id && (
              <button className="sa-btn sa-btn-success" onClick={handleReactivate}>
                <RefreshCw size={13} /> Reactivate
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="sa-two-col">
        {/* Company Information */}
        <div className="sa-card">
          <div className="sa-card-header">
            <h3 className="sa-card-title"><Building2 size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Company Information</h3>
          </div>
          <div className="sa-card-body">
            <div className="sa-detail-grid">
              <div className="sa-detail-item">
                <span className="sa-detail-label">Company Name</span>
                <span className="sa-detail-value">{company.name}</span>
              </div>
              <div className="sa-detail-item">
                <span className="sa-detail-label">Email</span>
                <span className="sa-detail-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Mail size={12} style={{ color: '#94a3b8' }} /> {company.email || '—'}
                </span>
              </div>
              <div className="sa-detail-item">
                <span className="sa-detail-label">Phone</span>
                <span className="sa-detail-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Phone size={12} style={{ color: '#94a3b8' }} /> {company.phone || '—'}
                </span>
              </div>
              <div className="sa-detail-item">
                <span className="sa-detail-label">Country</span>
                <span className="sa-detail-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Globe size={12} style={{ color: '#94a3b8' }} /> {company.country || '—'}
                </span>
              </div>
              <div className="sa-detail-item">
                <span className="sa-detail-label">Base Currency</span>
                <span className="sa-detail-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <select
                    className="sa-input sa-select"
                    style={{ padding: '4px 8px', fontSize: 12, width: 130 }}
                    value={currencyCode}
                    onChange={e => setCurrencyCode(e.target.value)}
                    disabled={savingCurrency}
                  >
                    {(currencies.length ? currencies : [{ code: currencyCode || 'USD', name: '' }]).map(c => (
                      <option key={c.code} value={c.code}>{c.code}{c.name ? ` — ${c.name}` : ''}</option>
                    ))}
                  </select>
                  <button
                    className="sa-btn sa-btn-primary sa-btn-sm"
                    onClick={handleSaveCurrency}
                    disabled={savingCurrency || !currencyCode || currencyCode === (company.currency || company.base_currency)}
                  >
                    {savingCurrency ? '...' : 'Save'}
                  </button>
                </span>
              </div>
              <div className="sa-detail-item">
                <span className="sa-detail-label">Registered</span>
                <span className="sa-detail-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Calendar size={12} style={{ color: '#94a3b8' }} /> {fmtDate(company.created_at)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Information */}
        <div className="sa-card">
          <div className="sa-card-header">
            <h3 className="sa-card-title"><CreditCard size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Subscription Details</h3>
          </div>
          <div className="sa-card-body">
            {company.sub_id ? (
              <div className="sa-detail-grid">
                <div className="sa-detail-item">
                  <span className="sa-detail-label">Plan</span>
                  <span className="sa-detail-value">{company.plan_name}</span>
                </div>
                <div className="sa-detail-item">
                  <span className="sa-detail-label">Status</span>
                  <span className={`sa-badge-status ${st.className}`}>{st.label}</span>
                </div>
                <div className="sa-detail-item">
                  <span className="sa-detail-label">Billing Cycle</span>
                  <span className="sa-detail-value" style={{ textTransform: 'capitalize' }}>{company.billing_cycle || '—'}</span>
                </div>
                <div className="sa-detail-item">
                  <span className="sa-detail-label">Current Period End</span>
                  <span className="sa-detail-value">{fmtDate(company.current_period_end)}</span>
                </div>
                <div className="sa-detail-item">
                  <span className="sa-detail-label">Trial Ends</span>
                  <span className="sa-detail-value">{fmtDate(company.trial_ends_at)}</span>
                </div>
                <div className="sa-detail-item">
                  <span className="sa-detail-label">Users</span>
                  <span className="sa-detail-value">
                    <Users size={12} style={{ color: '#94a3b8', marginRight: 4 }} />
                    {company.active_users || 0} / {company.max_users || '∞'}
                  </span>
                </div>
                <div className="sa-detail-item">
                  <span className="sa-detail-label">Invoices This Month</span>
                  <span className="sa-detail-value">
                    {company.invoices_this_month || 0} / {company.max_invoices_month === -1 ? '∞' : company.max_invoices_month || '∞'}
                  </span>
                </div>
                <div className="sa-detail-item">
                  <span className="sa-detail-label">Max Storage</span>
                  <span className="sa-detail-value">{company.max_storage_mb || '—'} MB</span>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>
                <AlertTriangle size={24} style={{ margin: '0 auto 8px', display: 'block' }} />
                <p style={{ fontSize: 13, margin: 0 }}>No active subscription. Assign a plan to get started.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Usage Summary */}
      <div className="sa-card" style={{ marginTop: 16 }}>
        <div className="sa-card-header">
          <h3 className="sa-card-title">Usage Summary</h3>
        </div>
        <div className="sa-card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
            {[
              { label: 'Active Users', value: `${company.active_users || 0}`, max: company.max_users, icon: Users },
              { label: 'Invoices/Month', value: `${company.invoices_this_month || 0}`, max: company.max_invoices_month, icon: CreditCard },
              { label: 'Storage Used', value: `${company.storage_used_mb || 0} MB`, max: company.max_storage_mb, icon: null },
            ].map((item, i) => {
              const pct = item.max && item.max > 0 ? Math.min(100, (parseInt(item.value) / item.max) * 100) : 0;
              const overLimit = pct > 90;
              return (
                <div key={i} style={{
                  padding: 16, borderRadius: 8,
                  border: `1px solid ${overLimit ? '#fecaca' : '#e2e8f0'}`,
                  background: overLimit ? '#fef2f2' : '#f8fafc',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: overLimit ? '#dc2626' : '#0f172a' }}>
                    {item.value}
                    {item.max && <span style={{ fontSize: 12, fontWeight: 500, color: '#94a3b8' }}> / {item.max === -1 ? '∞' : item.max}</span>}
                  </div>
                  {item.max && item.max > 0 && (
                    <div style={{ marginTop: 8, height: 4, background: '#e2e8f0', borderRadius: 2 }}>
                      <div style={{
                        height: '100%', borderRadius: 2,
                        width: `${Math.min(100, pct)}%`,
                        background: overLimit ? '#dc2626' : pct > 70 ? '#f59e0b' : '#3b82f6',
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Activity Timeline */}
      {activity.length > 0 && (
        <div className="sa-card" style={{ marginTop: 16 }}>
          <div className="sa-card-header">
            <h3 className="sa-card-title">Activity History</h3>
          </div>
          <div className="sa-card-body" style={{ padding: '12px 20px' }}>
            <div className="sa-timeline">
              {activity.slice(0, 10).map((a, i) => (
                <div key={i} className="sa-timeline-item">
                  <div className="sa-timeline-dot" style={{
                    background: a.action === 'activated' || a.action === 'confirmed' ? '#16a34a' :
                                a.action === 'cancelled' ? '#ef4444' : '#3b82f6'
                  }} />
                  <div className="sa-timeline-content">
                    <div className="sa-timeline-text">
                      <strong>{a.action}</strong>
                      {a.performed_by_name && <span style={{ color: '#94a3b8' }}> by {a.performed_by_name}</span>}
                    </div>
                    <div className="sa-timeline-time">{fmtDate(a.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
